// Turn reports into rows for Excel (CSV). Pure: no browser, no network. Money is written as plain
// Kwacha text ("1250.50") built from integer ngwee with no floating point, so Excel sorts and sums it.
import type { CsvCell } from './csv'
import type { DepartmentMonthSummary, DepartmentSummary, MonthStatement, QuarterReport } from '@/data/reports'

export function plainKwacha(ngwee: number): string {
  const neg = ngwee < 0
  const abs = Math.abs(ngwee)
  const whole = Math.floor(abs / 100)
  const frac = abs % 100
  return `${neg ? '-' : ''}${whole}.${frac < 10 ? '0' : ''}${frac}`
}

const k = plainKwacha
const blank: CsvCell[] = []

export function quarterReportRows(r: QuarterReport, departmentName: string): CsvCell[][] {
  const rows: CsvCell[][] = [
    ['Quarterly report', departmentName, r.quarterId],
    ['All amounts in Kwacha'],
    blank,
    ['MONEY IN (dues and other income)'],
    ['Month', 'Amount'],
    ...r.income.byMonth.map((m) => [m.month, k(m.ngwee)]),
    ['Total', k(r.income.totalNgwee)],
    blank,
    ['BUDGET AGAINST WHAT WAS SPENT'],
  ]
  if (r.budget) {
    rows.push(['Line', 'Category', 'Budgeted', 'Spent', 'Waiting or approved', 'Left'])
    for (const l of r.budget.lines) rows.push([l.description, l.categoryName, k(l.budgetedNgwee), k(l.actualNgwee), k(l.committedNgwee), k(l.remainingNgwee)])
    rows.push(['Total', '', k(r.budget.budgetedNgwee), k(r.budget.actualNgwee), k(r.budget.committedNgwee), k(r.budget.budgetedNgwee - r.budget.actualNgwee - r.budget.committedNgwee)])
  } else {
    rows.push(['No approved budget for this quarter'])
  }
  rows.push(blank, ['SPENDING BY CATEGORY'], ['Category', 'Amount'])
  for (const c of r.spending.byCategory) rows.push([c.category, k(c.ngwee)])
  rows.push(['Total paid', k(r.spending.paidNgwee)], ['Of which not in the budget', k(r.spending.unbudgetedNgwee)], ['Waiting for approval or payment', k(r.spending.waitingNgwee)])
  rows.push(blank, ['MONEY SENT TO CENTRAL TREASURY'], ['Date', 'How', 'Declared', 'Confirmed received', 'Status'])
  for (const m of r.remittances.items) rows.push([m.movedOn, m.method, k(m.declaredNgwee), k(m.confirmedNgwee), m.status])
  rows.push(['Total', '', k(r.remittances.declaredNgwee), k(r.remittances.confirmedNgwee)])
  if (r.position) {
    rows.push(blank, ['CASH POSITION'], ['Collected this quarter', k(r.position.collectedNgwee)], ['Spent from department cash', k(r.position.spentLocalNgwee)], ['Sent to central treasury', k(r.position.remittedNgwee)], ['Cash the department should hold', k(r.position.cashOnHandNgwee)])
  }
  rows.push(blank, ['DUES OWED RIGHT NOW'], ['Members behind', r.arrears.members], ['Total owed', k(r.arrears.totalNgwee)])
  return rows
}

export function churchSummaryRows(rows: DepartmentSummary[], quarterId: string): CsvCell[][] {
  const sum = (f: (d: DepartmentSummary) => number) => rows.reduce((s, d) => s + f(d), 0)
  return [
    ['Church summary', quarterId],
    ['All amounts in Kwacha'],
    blank,
    ['Department', 'Collected', 'Spent', 'Sent to central', 'Confirmed received', 'Not yet confirmed', 'Budgeted', 'Spent against budget', 'Cash on hand', 'Dues owed'],
    ...rows.map((d) => [d.name, k(d.collectedNgwee), k(d.spentNgwee), k(d.sentNgwee), k(d.confirmedNgwee), k(d.unconfirmedNgwee), k(d.budgetedNgwee), k(d.budgetActualNgwee), k(d.cashOnHandNgwee), k(d.arrearsNgwee)]),
    ['Total', k(sum((d) => d.collectedNgwee)), k(sum((d) => d.spentNgwee)), k(sum((d) => d.sentNgwee)), k(sum((d) => d.confirmedNgwee)), k(sum((d) => d.unconfirmedNgwee)), k(sum((d) => d.budgetedNgwee)), k(sum((d) => d.budgetActualNgwee)), k(sum((d) => d.cashOnHandNgwee)), k(sum((d) => d.arrearsNgwee))],
  ]
}

export function monthStatementRows(r: MonthStatement, departmentName: string): CsvCell[][] {
  const rows: CsvCell[][] = [
    ['Monthly statement', departmentName, r.monthId],
    ['All amounts in Kwacha'],
    blank,
    ['MONEY IN'],
    ['Kind', 'Amount'],
    ...r.income.byKind.map((x) => [x.kind, k(x.ngwee)]),
    ['Total', k(r.income.totalNgwee)],
    blank,
    ['SPENDING BY CATEGORY'],
    ['Category', 'Amount'],
    ...r.spending.byCategory.map((x) => [x.category, k(x.ngwee)]),
    ['Total paid', k(r.spending.paidNgwee)],
    ['Of which not in the budget', k(r.spending.unbudgetedNgwee)],
    ['Waiting for approval or payment', k(r.spending.waitingNgwee)],
    blank,
    ['MONEY SENT TO CENTRAL TREASURY'],
    ['Date', 'How', 'Declared', 'Confirmed received', 'Status'],
    ...r.remittances.items.map((m) => [m.movedOn, m.method, k(m.declaredNgwee), k(m.confirmedNgwee), m.status]),
    ['Total', '', k(r.remittances.declaredNgwee), k(r.remittances.confirmedNgwee)],
    blank,
    ['CHANGE IN DEPARTMENT CASH THIS MONTH'],
    ['Collected', k(r.cash.collectedNgwee)],
    ['Spent from department cash', k(r.cash.spentFromCashNgwee)],
    ['Sent to central treasury', k(r.cash.sentNgwee)],
    ['Received from central treasury', k(r.cash.receivedFromCentralNgwee)],
    ['Net change', k(r.cash.changeNgwee)],
    blank,
    ['DUES OWED RIGHT NOW'],
    ['Members behind', r.arrears.members],
    ['Total owed', k(r.arrears.totalNgwee)],
  ];
  return rows;
}

export function churchMonthRows(rows: DepartmentMonthSummary[], monthId: string): CsvCell[][] {
  const sum = (f: (d: DepartmentMonthSummary) => number) => rows.reduce((s, d) => s + f(d), 0)
  return [
    ['Church summary, month', monthId],
    ['All amounts in Kwacha'],
    blank,
    ['Department', 'Collected', 'Spent', 'Sent to central', 'Confirmed received', 'Not yet confirmed', 'Net change in cash', 'Dues owed (now)'],
    ...rows.map((d) => [d.name, k(d.collectedNgwee), k(d.spentNgwee), k(d.sentNgwee), k(d.confirmedNgwee), k(d.unconfirmedNgwee), k(d.cashChangeNgwee), k(d.arrearsNgwee)]),
    ['Total', k(sum((d) => d.collectedNgwee)), k(sum((d) => d.spentNgwee)), k(sum((d) => d.sentNgwee)), k(sum((d) => d.confirmedNgwee)), k(sum((d) => d.unconfirmedNgwee)), k(sum((d) => d.cashChangeNgwee)), k(sum((d) => d.arrearsNgwee))],
  ]
}
